import { UploadExcelError } from '@/constant';
import { PayloadAction } from '@reduxjs/toolkit';
import { put } from 'redux-saga/effects';

import * as API from 'services';

import {
	formatUploadExcelAssignmentFailedList,
	formatUploadExcelPublicAssignmentFailedList,
	formatWorkerAssignmentApplicantList,
	formatWorkerAssignmentDetail,
	formatWorkerAssignmentExcelList,
	formatWorkerAssignmentList,
} from './worker-assignment.helper';
import { WorkerAssignmentActions } from './worker-assignment.redux';
import {
	IPayloadDeleteWorkerAssignmentRequest,
	IPayloadGetWorkerAssignmentApplicantListRequest,
	IPayloadGetWorkerAssignmentDetailRequest,
	IPayloadGetWorkerAssignmentEvaluationRequest,
	IPayloadGetWorkerAssignmentExcelListRequest,
	IPayloadGetWorkerAssignmentListRequest,
	IPayloadWorkerAssignmentApplicantStatusRequest,
	TPayloadUploadExcelAssignmentRequest,
	TPayloadUploadExcelPublicAssignmentRequest,
} from './worker-assignment.type';

export function* getWorkerAssignmentListSaga(
	action: PayloadAction<IPayloadGetWorkerAssignmentListRequest>
) {
	const { callback, ...rest } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerAssignmentListResponse> =
			yield API.getWorkerAssignmentList({ ...rest });

		const content = formatWorkerAssignmentList(res?.data?.content);

		yield put(
			WorkerAssignmentActions.getWorkerAssignmentListSuccess({
				...rest,
				content,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerAssignmentActions.getWorkerAssignmentListFailure());
	}
}

export function* getWorkerAssignmentExcelListSaga(
	action: PayloadAction<IPayloadGetWorkerAssignmentExcelListRequest>
) {
	const { callback, ...rest } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerAssignmentListResponse> =
			yield API.getWorkerAssignmentList({ ...rest });

		const content = formatWorkerAssignmentExcelList(res?.data?.content);

		yield put(WorkerAssignmentActions.getWorkerAssignmentExcelListSuccess());
		callback?.({ data: content });
	} catch (error) {
		callback?.({ error });
		yield put(WorkerAssignmentActions.getWorkerAssignmentExcelListFailure());
	}
}

export function* deleteWorkerAssignments(
	action: PayloadAction<IPayloadDeleteWorkerAssignmentRequest>
) {
	const { callback, ids } = action.payload;

	try {
		yield API.deleteWorkerAssignment(ids);

		yield put(WorkerAssignmentActions.deleteWorkerAssignmentSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerAssignmentActions.deleteWorkerAssignmentFailure());
	}
}

export function* getWorkerAssignmentDetail(
	action: PayloadAction<IPayloadGetWorkerAssignmentDetailRequest>
) {
	const { id, isAudit, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerAssignmentDetailResponse> =
			yield API.getWorkerAssignmentDetail(id, isAudit);

		const workerAssignmentDetail = formatWorkerAssignmentDetail(res.data);

		yield put(
			WorkerAssignmentActions.getWorkerAssignmentDetailSuccess({
				workerAssignmentDetail,
			})
		);
		callback?.({
			data: workerAssignmentDetail,
		});
	} catch (error) {
		callback?.({ error });
		yield put(WorkerAssignmentActions.getWorkerAssignmentDetailFailure());
	}
}

export function* getWorkerAssignmentApplicantListSaga(
	action: PayloadAction<IPayloadGetWorkerAssignmentApplicantListRequest>
) {
	const { callback, ...rest } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerAssignmentApplicantListResponse> =
			yield API.getWorkerAssignmentApplicantList({ ...rest });

		const content = formatWorkerAssignmentApplicantList(res?.data?.content);

		yield put(
			WorkerAssignmentActions.getWorkerAssignmentApplicantListSuccess({
				...rest,
				content,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerAssignmentActions.getWorkerAssignmentListFailure());
	}
}

export function* updateWorkerAssignmentApplicantStatusSaga(
	action: PayloadAction<IPayloadWorkerAssignmentApplicantStatusRequest>
) {
	const { callback, data } = action.payload;

	try {
		yield API.updateWorkerAssignmentApplicantStatus(data);

		yield put(
			WorkerAssignmentActions.updateWorkerAssignmentApplicantStatusSuccess()
		);

		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(
			WorkerAssignmentActions.updateWorkerAssignmentApplicantStatusFailure()
		);
	}
}

export function* getWorkerAssignmentEvaluationSaga(
	action: PayloadAction<IPayloadGetWorkerAssignmentEvaluationRequest>
) {
	const { callback, id } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.IWorkerAssignmentEvaluation> =
			yield API.getWorkerAssignmentEvaluation(id);

		const evaluation = res.data;

		yield put(
			WorkerAssignmentActions.getWorkerAssignmentEvaluationSuccess({
				evaluation,
			})
		);

		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerAssignmentActions.getWorkerAssignmentEvaluationFailure());
	}
}

export function* uploadExcelAssignmentSaga(
	action: PayloadAction<TPayloadUploadExcelAssignmentRequest>
) {
	const { data, callback, hasAlert62, errorList } = action.payload;
	const programName = data[0]?.programName;

	try {
		const res: API.ApiResponseSuccess<API.TUploadExcelAssignmentResponse> =
			yield API.uploadExcelAssignment(data, hasAlert62 ? errorList : []);

		// BE can return error data by group of franchise
		const sortedDataByNo = res?.data?.sort((a, b) => a?.data?.no - b?.data?.no);

		const failedList = formatUploadExcelAssignmentFailedList(
			sortedDataByNo,
			programName
		);

		const successCount = data?.length - (failedList?.length || 0);
		const failCount = failedList?.length || 0;

		yield put(
			WorkerAssignmentActions.uploadExcelAssignmentSuccess({
				failedList,
				successCount,
				failCount,
				isUploadExcelProgramNameNotFound: false,
				hasAlert62,
			})
		);

		let hasAlert62Arr: (API.TUploadExcelAssignmentResponse[number]['data'] & {
			shouldModifyDistrict: boolean;
		})[] = [];

		let newErrorList: API.TUploadExcelAssignmentFailedItem[] = [];

		res?.data?.forEach((item) => {
			if (
				item?.errorList?.length === 1 &&
				item?.errorList?.[0]?.reason === UploadExcelError.EXCEL_AM_6
			) {
				hasAlert62Arr.push({
					...item?.data,
					shouldModifyDistrict: true,
				});
			} else {
				newErrorList.push(item);
			}
		});

		callback?.({
			failCount,
			successCount,
			hasAlert62Arr,
			errorList: newErrorList,
		});
	} catch (error: any) {
		if (
			error?.response?.status === 400 &&
			error?.response?.data?.messageCode ===
				UploadExcelError.AM1_PROGRAM_NOT_FOUND
		) {
			const errorData: API.TUploadExcelAssignmentFailedItem[] =
				error?.response?.data?.errorData?.data;
			// BE can return error data by group of franchise
			const sortedErrorData = errorData?.sort(
				(a, b) => a?.data?.no - b?.data?.no
			);
			const programName: string = error?.response?.data?.errorData?.programName;

			const failedList = formatUploadExcelAssignmentFailedList(
				sortedErrorData,
				programName
			);
			yield put(
				WorkerAssignmentActions.uploadExcelAssignmentSuccess({
					failedList,
					successCount: 0,
					failCount: sortedErrorData?.length,
					isUploadExcelProgramNameNotFound: true,
				})
			);
			callback?.({
				failCount: sortedErrorData?.length,
				successCount: 0,
			});
		} else {
			callback?.({
				error,
				failCount: 0,
				successCount: 0,
			});
			yield put(WorkerAssignmentActions.uploadExcelAssignmentFailure());
		}
	}
}

export function* uploadExcelPublicAssignmentSaga(
	action: PayloadAction<TPayloadUploadExcelPublicAssignmentRequest>
) {
	const { data, callback } = action.payload;
	const programName = data[0]?.programName;

	try {
		const res: API.ApiResponseSuccess<API.TUploadExcelPublicAssignmentResponse> =
			yield API.uploadExcelPublicAssignment(data);

		// BE can return error data by group of franchise
		const sortedDataByNo = res?.data?.sort((a, b) => a?.data?.no - b?.data?.no);

		const failedList = formatUploadExcelPublicAssignmentFailedList(
			sortedDataByNo,
			programName
		);

		const successCount = data?.length - (failedList?.length || 0);
		const failCount = failedList?.length || 0;

		yield put(
			WorkerAssignmentActions.uploadExcelPublicAssignmentSuccess({
				failedList,
				successCount,
				failCount,
				isUploadExcelProgramNameNotFound: false,
			})
		);
		callback?.({
			failCount,
			successCount,
		});
	} catch (error: any) {
		if (
			error?.response?.status === 400 &&
			error?.response?.data?.messageCode ===
				UploadExcelError.AM1_PROGRAM_NOT_FOUND
		) {
			const errorData: API.TUploadExcelPublicAssignmentFailedItem[] =
				error?.response?.data?.errorData?.data;
			// BE can return error data by group of franchise
			const sortedErrorData = errorData?.sort(
				(a, b) => a?.data?.no - b?.data?.no
			);
			const programName: string = error?.response?.data?.errorData?.programName;

			const failedList = formatUploadExcelPublicAssignmentFailedList(
				sortedErrorData,
				programName
			);
			yield put(
				WorkerAssignmentActions.uploadExcelPublicAssignmentSuccess({
					failedList,
					successCount: 0,
					failCount: sortedErrorData?.length,
					isUploadExcelProgramNameNotFound: true,
				})
			);
			callback?.({
				failCount: sortedErrorData?.length,
				successCount: 0,
			});
		} else {
			callback?.({
				error,
				failCount: 0,
				successCount: 0,
			});
			yield put(WorkerAssignmentActions.uploadExcelPublicAssignmentFailure());
		}
	}
}
